import { useEffect, useState } from "react";
import client from "../lib/api";
import { Calendar } from "@phosphor-icons/react";

/**
 * POD Age Heatmap.
 *
 * 7-row × N-week calendar showing daily POD uploads over the last 90 days.
 * Ops uses this to spot which weekdays leak the most PODs — e.g. if
 * Fridays consistently have low counts, drivers aren't capturing PODs
 * before the weekend and paperwork rots in AR.
 *
 * Auto-hides on fresh accounts with zero PODs in the window.
 */
const _WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

export default function PodAgeHeatmap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/pod/heatmap?days=90")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data || !data.buckets?.length || data.total === 0) return null;

  const buckets = data.buckets;
  const max = Math.max(1, ...buckets.map((b) => b.count));

  // Group buckets into weekly columns (7 rows). Each column = a Monday-Sunday
  // window. The window starts on the first bucket's actual weekday, so we
  // pad the leading column with blanks.
  const firstWeekday = buckets[0].weekday;
  const paddedFront = new Array(firstWeekday).fill(null);
  const cells = [...paddedFront, ...buckets];
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  // Compute PODs-per-weekday for the trailing legend.
  const byWeekday = new Array(7).fill(0);
  buckets.forEach((b) => { byWeekday[b.weekday] += b.count; });

  return (
    <div
      data-testid="pod-age-heatmap"
      className="rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:p-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="shrink-0 w-8 h-8 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <Calendar size={16} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
            POD age · last 90 days
          </div>
          <div className="text-sm text-white">
            <span data-testid="pod-heatmap-total" className="tabular-nums font-semibold">
              {data.total}
            </span>{" "}
            uploads · brighter = busier day
          </div>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {/* Weekday labels column */}
        <div className="flex flex-col gap-0.5 pt-0.5 pr-1 shrink-0">
          {_WEEKDAY_LABELS.map((wd, i) => (
            <div
              key={i}
              className="h-3 text-[8px] text-neutral-600 tabular-nums flex items-center"
              style={{ width: "10px" }}
            >
              {i % 2 === 0 ? wd : ""}
            </div>
          ))}
        </div>
        {/* Grid */}
        <div
          data-testid="pod-heatmap-grid"
          className="flex gap-0.5"
        >
          {weeks.map((week, ci) => (
            <div key={ci} className="flex flex-col gap-0.5">
              {week.map((cell, ri) => {
                if (!cell) {
                  return <div key={ri} className="w-3 h-3 rounded-sm bg-transparent" />;
                }
                const intensity = cell.count === 0 ? 0.05 : 0.15 + (cell.count / max) * 0.85;
                return (
                  <div
                    key={ri}
                    data-testid={`pod-heatmap-cell-${cell.date}`}
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: `rgba(255, 90, 31, ${intensity})` }}
                    title={`${cell.date} · ${cell.count} POD${cell.count === 1 ? "" : "s"}`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Per-weekday totals summary */}
      <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-neutral-600">
          By weekday
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="pod-heatmap-by-weekday">
          {_WEEKDAY_LABELS.map((wd, i) => {
            const dayName = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i];
            const count = byWeekday[i];
            const isLowest = count === Math.min(...byWeekday) && count > 0;
            return (
              <div
                key={i}
                className={`text-[10px] flex items-center gap-1 ${
                  isLowest ? "text-red-400" : "text-neutral-400"
                }`}
                title={`${dayName}: ${count} POD${count === 1 ? "" : "s"}`}
              >
                <span className="font-mono">{dayName[0]}</span>
                <span className="tabular-nums">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
